import { Component, forwardRef, inject, OnInit, Provider, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, NG_VALUE_ACCESSOR, ValidationErrors } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { isEqual } from 'lodash';
import {
  AlertComponent,
  CardComponent,
  FormGroupComponent,
  LoaderComponent,
  ModalService,
  RadioComponent,
  ToastService,
  TreeComponent,
  TreeElement,
  TreeElementDirective
} from 'mbs-ui-kit';
import { ExtendedTreeElement, StepsHelpers } from 'wizards/helpers/steps-helpers';
import { RestoreDisksStepFormValue, RestoreDisksStepValue } from 'wizards/models/restore-disks-models';
import { VirtualDisksSelectedType } from 'wizards/models/select-virtual-disks-models';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import { ParamsForVMDiskList, WizardStepsService } from 'wizards/services/wizard-steps.service';
import { BaseForRestoreSourceOrDisksStep } from 'wizards/steps/only-restore-steps/base/base-for-restore-source-or-disks-step.directive';

import { I18NextPipe } from 'angular-i18next';

import { CommonModule } from '@angular/common';

import { ReactiveFormsModule } from '@angular/forms';

const VALID_VERSION_FOR_GET_DISKS = 812126;

const RestoreDisksStepValueAccessor: Provider = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  useExisting: forwardRef(() => RestoreDisksStepComponent),
  multi: true
};

@UntilDestroy()
@Component({
  selector: 'mbs-restore-disks-step',
  templateUrl: './restore-disks-step.component.html',
  styleUrls: ['./restore-disks-step.component.scss'],
  providers: [RestoreDisksStepValueAccessor],
  imports: [
    AlertComponent,
    CardComponent,
    CommonModule,
    FormGroupComponent,
    I18NextPipe,
    LoaderComponent,
    RadioComponent,
    ReactiveFormsModule,
    TreeComponent,
    TreeElementDirective
  ]
})
export class RestoreDisksStepComponent extends BaseForRestoreSourceOrDisksStep<RestoreDisksStepValue> implements OnInit {
  mainService: RemoteManagementWizardsService;
  modal: ModalService;
  stepService: WizardStepsService;
  toastService: ToastService;

  @ViewChild(TreeComponent, { static: false }) treeComponent: TreeComponent;

  public readonly virtualDisksSelectedType = VirtualDisksSelectedType;

  public isDisks = true;
  public disabledTree = false;

  private initialElements = [];
  private filteredPrev: TreeElement[] = [];

  constructor() {
    const mainService = inject(RemoteManagementWizardsService);
    const modal = inject(ModalService);
    const stepService = inject(WizardStepsService);
    const toastService = inject(ToastService);

    super();

    this.mainService = mainService;
    this.modal = modal;
    this.stepService = stepService;
    this.toastService = toastService;
  }

  ngOnInit(): void {
    this.initForm();
    this.setValues();

    this.validVersionForGetDisks = +this.mainService.backupVersionUpdated >= VALID_VERSION_FOR_GET_DISKS;
    this.stepService.treeData$.pipe(untilDestroyed(this)).subscribe({
      next: (data: { folders: string[]; treeData: TreeElement[] }) => {
        const filtered: TreeElement[] = data?.treeData?.filter((machine) => machine.checked || machine.indeterminate) || [];
        if (!isEqual(filtered, this.filteredPrev)) {
          this.filteredPrev = filtered;
          this.loadDisks(filtered);
        }
      }
    });
  }

  initForm(): void {
    this.stepForm = new FormGroup<RestoreDisksStepFormValue>({
      excludes: new FormControl([]),
      folders: new FormControl([], [this.foldersRequiredValidators.bind(this)]),
      type: new FormControl(VirtualDisksSelectedType.Selected)
    });

    this.initFormEvents();
  }

  foldersRequiredValidators(control: AbstractControl): ValidationErrors | null {
    if (control?.value?.length || this.stepForm?.get('type')?.value === VirtualDisksSelectedType.All) return null;

    return { foldersRequired: true };
  }

  updateForm(value: RestoreDisksStepValue): void {
    this.stepForm.reset(value);

    this.stepForm.get('folders').updateValueAndValidity();
  }

  loadDisks(treeElements: TreeElement[]): void {
    this.preparationOfParameters('');

    const excludes = this.stepForm.get('excludes').value;

    this.treeData = treeElements.map((item: ExtendedTreeElement) => {
      const pathArray = item.path.split(this.joinForInput);
      const newPath = this.validVersionForGetDisks && pathArray?.length > 5 ? pathArray.slice(5).join(this.joinForInput) : item.path;
      const existExc = excludes.some((exc) => !StepsHelpers.pathNotIncludePath(this.isUnix, this.joinForInput, exc, newPath));

      return { ...item, path: newPath, checked: !existExc, indeterminate: existExc, gotChildren: false, hideCheckbox: true };
    });

    if (this.validVersionForGetDisks) this.getDisksByMachines(treeElements);
  }

  virtualDiskRestoreTypeChangeHandler(selectedType: VirtualDisksSelectedType): void {
    this.disabledTree = selectedType === VirtualDisksSelectedType.All;

    this.stepForm?.get('folders')?.updateValueAndValidity();
  }

  changeTreeHandler(event: ExtendedTreeElement): void {
    super.changeTreeHandler(event);

    let existUncheck = false;
    const excludes: string[] = [];

    this.treeComponent.data.forEach((computer: ExtendedTreeElement) => {
      let someDisabled = false;
      const childrenLength = computer.children?.length || 0;

      if (childrenLength > 1) {
        computer.children?.forEach((disk: ExtendedTreeElement) => {
          if (disk.disableCheckbox) someDisabled = true;
          if (!disk.checked) {
            existUncheck = true;
            excludes.push(disk.restorePath || disk.path);
          }
        });
      }

      if (childrenLength - excludes?.length === 1) {
        computer.children?.forEach((d: ExtendedTreeElement) => d.checked && (d.disableCheckbox = true));
      } else if (someDisabled) computer.children?.forEach((d: ExtendedTreeElement) => d.disableCheckbox && (d.disableCheckbox = false));
    });

    this.stepForm.get('excludes').setValue(Array.from(new Set(excludes)));

    if (existUncheck) {
      this.stepForm.get('type').setValue(VirtualDisksSelectedType.Selected);
      this.virtualDiskRestoreTypeChangeHandler(VirtualDisksSelectedType.Selected);
    }
  }

  updateAfterLoadSubtree(treeData: ExtendedTreeElement[]): void {
    const foldersMap = {};
    const excludesMap = {};
    const newFolders: string[] = this.stepForm.get('folders')?.value || [];
    const excludes: string[] = this.stepForm.get('excludes')?.value || [];
    let checkedCount = 0;

    newFolders?.forEach((folder) => (foldersMap[folder] = true));
    excludes?.forEach((folder) => (excludesMap[folder] = true));

    treeData?.forEach((disk: ExtendedTreeElement) => {
      if (disk.checked) {
        if (checkedCount < 2) checkedCount++;
        if (!foldersMap[disk.path]) newFolders.push(disk.restorePath || disk.path);
      } else if (!excludesMap[disk.path]) excludes.push(disk.restorePath || disk.path);
    });

    if (checkedCount === 1) treeData?.forEach((disk: ExtendedTreeElement) => disk.checked && (disk.disableCheckbox = true));

    this.stepForm.get('folders').setValue(Array.from(new Set(newFolders)));
    this.stepForm.get('excludes').setValue(Array.from(new Set(excludes)));
  }

  private getDisksByMachines(treeElements: TreeElement[]): void {
    if (isEqual(this.initialElements, treeElements)) return;

    this.loadingTree = true;
    this.initialElements = treeElements;

    if (!treeElements?.length) return void (this.loadingTree = false);

    const treeParams: ParamsForVMDiskList = {
      agentType: 'backup',
      commandType: 'GetBackupVirtualMachineDiskList',
      params: {
        machineNames: treeElements.map((el: TreeElement) => el.label as string),
        path: this.resultPoint?.path,
        password: this.mainService?.password?.value || '',
        order: 'DisplayNameAsc',
        isAsync: true,
        SessionId: this.mainService.sessionId || null
      }
    };

    this.stepService
      .getRemoteCommandData(treeParams, this.mainService.hid)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (data): void => {
          if (!data?.data?.items?.length) return void (this.loadingTree = false);

          const result: TreeElement[] = [];
          const treeMap: { [key: string]: { root: TreeElement; children: unknown[] } } = this.treeData.reduce((acc, next) => {
            acc[next.label as string] = { root: next, children: [] };
            return acc;
          }, {});

          data.data.items.forEach((disk) => treeMap[disk.parentMachineName].children.push(disk));
          Object.values(treeMap).forEach((item) => {
            item.root.children = this.getTreeDataFromRequest({ data: { items: item.children } }, item.root);
            item.root.gotChildren = !!item.root.children?.length;
            item.root.expanded = item.root.gotChildren;
            result.push(item.root);
          });

          this.treeData = result;
          this.loadingTree = false;
        },
        error: () => (this.loadingTree = false)
      });
  }
}
